<template>
  <nav>
    <v-app-bar
      v-if="!mobile"
      color="header-background"
      height="86"
    >
      <v-row no-gutters justify="center">
        <v-col
          cols="12"
          md="10"
          class="v-toolbar__content d-flex justify-space-between"
        >
          <t-logo-header style="width: 160px" @click="$route.path !== '/' && $router.push('/')" />

          <v-spacer />

          <v-toolbar-items>
            <v-btn
              v-for="{ label, icon, callback } in menus"
              :key="label"
              variant="text"
              @click="callback"
            >
              <template #prepend>
                <v-icon>
                  {{ icon }}
                </v-icon>
              </template>
              {{ label }}
            </v-btn>
          </v-toolbar-items>
        </v-col>
      </v-row>
    </v-app-bar>
    <div v-else>
      <v-app-bar
        color="header-background"
        height="75"
        extension-height="50"
      >
        <v-app-bar-nav-icon @click.stop="drawer = !drawer" />

        <v-spacer />

        <v-app-bar-title style="min-width: 150px;">
          <t-logo-header style="height: 75px;" @click="$route.path !== '/' && $router.push('/')" />
        </v-app-bar-title>

        <v-spacer />

        <v-btn v-if="!logged" icon @click="() => dialog.show('/login')">
          <v-icon> mdi-account-outline </v-icon>
        </v-btn>
        <v-btn v-else icon @click="$router.push('/user/data')">
          <v-icon> mdi-account </v-icon>
        </v-btn>

        <template #extension>
          <v-sheet color="transparent" class="py-1" style="width: 100%">
            <v-row justify="space-between" align="center" class="mx-0">
              <v-col>
                <v-btn
                  v-if="showTicketsMenu"
                  style="text-transform: capitalize"
                  class="ps-1"
                  @click="$router.push('/ticket/payments')"
                >
                  <v-icon class="me-2" size="24">
                    mdi-ticket-confirmation-outline
                  </v-icon>
                  {{ $t("menu.tickets") }}
                </v-btn>
              </v-col>
              <v-col
                v-if="logged"
                class="text-end"
                style="font-size: 14px; font-weight: 500"
              >
                Olá {{ name ? name.split(" ")[0] : name }}!
              </v-col>
            </v-row>
          </v-sheet>
        </template>
      </v-app-bar>

      <v-navigation-drawer v-model="drawer" location="left">
        <v-toolbar
          flat
          density="compact"
          color="primary"
        >
          <v-toolbar-title v-if="logged">
            Olá
            {{ name ? name.split(" ")[0] : name }}
          </v-toolbar-title>
          <v-toolbar-title v-else />
        </v-toolbar>
        <v-list nav density="compact">
          <v-list-item
            v-for="{ label, icon, classes, callback } in menus"
            :key="label"
            :class="classes"
            @click="
              drawer = false;
              callback();
            "
          >
            <template #prepend>
              <v-icon>
                {{ icon }}
              </v-icon>
            </template>
            <v-list-item-title> {{ label }} </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import TLogoHeader from "@/components/core/TLogoHeader.vue";
import type AppBarItem from "@/types/AppBarItem";
import { useDialog } from "@/stores/dialog";
import { useAppStore } from "@/stores/app";
import { useUserStore } from "@/stores/user";
import { useCartStore } from "@/stores/cart";
import { useCheckoutStore } from "@/stores/checkout";

const router = useRouter();
const route = useRoute();
const app = useAppStore();
const user = useUserStore();
const cart = useCartStore();
const checkout = useCheckoutStore();
const { mobile } = useDisplay();
const { t } = useI18n();
const dialog = useDialog();

const drawer = ref(false);

const name = computed<string>(() => user.data.name);
const logged = computed<boolean>(() => user.data.name !== undefined);

const showTicketsMenu = computed<boolean>(() => {
  if (route.name === null) return true;
  if (route.name === undefined) return true;

  const exclude = [ "" ];

  return !exclude.includes(route.name as string);
});

const menus = computed<AppBarItem[]>(() => {
  if (mobile.value) {
    return [
      {
        label: "Home",
        icon: "mdi-home-outline",
        callback: () => router.push("/"),
        condition: true,
      },
      {
        label: t("menu.tickets"),
        icon: "mdi-ticket-confirmation-outline",
        callback: () => router.push("/ticket/payments"),
        condition: true,
      },
      {
        label: "Meus Dados",
        icon: "mdi-pencil",
        callback: () => router.push("/user/data"),
        condition: user.logged,
      },
      {
        label: "Acesse Sua Conta",
        icon: "mdi-account-outline",
        callback: () => dialog.show("/login"),
        condition: !user.logged,
      },
      {
        label: "Fale conosco",
        icon: "mdi-chat-question-outline",
        callback: () => router.push("/suporte"),
        condition: true,
      },
      {
        label: "Instalar APP",
        icon: "mdi-cellphone-arrow-down",
        classes: "browser-only",
        callback: () => app.promptPwaInstall(),
        condition: window.pwa !== null && !app.pwa.installed,
      },
      {
        label: "Sair",
        icon: "mdi-logout",
        callback: () => {
          user.logout();
          cart.reset();
          checkout.reset();
          router.push("/");
        },
        condition: user.logged,
      },
    ].filter((item: AppBarItem) => item.condition);
  } else {
    return [
      {
        label: "Home",
        icon: "mdi-home-outline",
        callback: () => router.push("/"),
        condition: true,
      },
      {
        label: t("menu.tickets"),
        icon: "mdi-ticket-confirmation-outline",
        callback: () => router.push("/ticket/payments"),
        condition: true,
      },
      {
        label: "Meus Dados",
        icon: "mdi-pencil",
        callback: () => router.push("/user/data"),
        condition: user.logged,
      },
      {
        label: "Acesse Sua Conta",
        icon: "mdi-account-outline",
        callback: () => dialog.show("/login"),
        condition: !user.logged,
      },
      {
        label: `Olá ${
          user.data.name ? user.data.name.split(" ")[0] : user.data.name
        }!`,
        icon: "mdi-account-outline",
        callback: () => true,
        condition: user.logged,
      },
      {
        label: "Sair",
        icon: "mdi-logout",
        callback: () => {
          user.logout();
          cart.reset();
          checkout.reset();
          router.push("/");
        },
        condition: user.logged,
      },
    ].filter((item: AppBarItem) => item.condition);
  }
});

defineOptions({
  name: "TheAppBar",
});
</script>

<style scoped>
.v-toolbar__content > .v-toolbar-title {
  margin: 0 !important;
}
</style>
